import React from "react";
import "./InternationalRoaming.css";

const roamingPacks = [
  {
    code: "ROAM5",
    name: "5GB Travel Pack",
    data: "5GB Data",
    calls: "30 Minutes",
    sms: "30 SMS",
    validity: "7 Day Validity",
    price: "$45",
    smsText: "Text roam5 to 179",
  },
  {
    code: "ROAM10",
    name: "10GB Travel Pack",
    data: "10GB Data",
    calls: "60 Minutes",
    sms: "60 SMS",
    validity: "14 Day Validity",
    price: "$75",
    smsText: "Text roam10 to 179",
  },
  {
    code: "ROAM3",
    name: "3GB Travel BizPack",
    data: "3GB Data",
    calls: "300 Minutes",
    sms: "300 SMS",
    validity: "3 Day Validity",
    price: "$85",
    smsText: "Text roam3 to 179",
  },
];

export default function InternationalRoaming(): JSX.Element {
  return (
    <main className="intl-page">

      {/* BACK BUTTON */}
      <div className="intl-back-wrapper">
        <a href="/plans/mobile" className="intl-back-btn">
          ← Back to Mobile Plans
        </a>
      </div>

      {/* HERO */}
      <section className="intl-hero">
        <div className="intl-hero-inner">
          <h1>International Roaming</h1>
          <p>
            Stay connected when you travel overseas with simple roaming packs,
            clear activation steps and tips to avoid bill shock.
          </p>
        </div>
      </section>

      <div className="intl-container">

        {/* ROAMING PACKS */}
        <section className="intl-section">
          <h2>Roaming Packs</h2>
          <p className="intl-muted">
            Choose a roaming pack that suits your trip length and usage, then
            activate it by SMS before using your service overseas.
          </p>

          <div className="intl-pack-grid">
            {roamingPacks.map((p) => (
              <div key={p.code} className="intl-pack-card">
                <div className="intl-pack-header">
                  <div className="intl-pack-code">{p.code}</div>
                  <div className="intl-pack-name">{p.name}</div>
                </div>

                <div className="intl-pack-body">
                  <ul>
                    <li>{p.data}</li>
                    <li>{p.calls}</li>
                    <li>{p.sms}</li>
                    <li>{p.validity}</li>
                  </ul>
                </div>

                <div className="intl-pack-footer">
                  <div className="intl-pack-price">{p.price}</div>
                  <div className="intl-pack-sms">{p.smsText}</div>
                </div>
              </div>
            ))}
          </div>

          <p className="intl-gst-note">All roaming pack prices include GST.</p>
        </section>


        {/* HOW TO ACTIVATE */}
        <section className="intl-section">
          <h2>How to Activate</h2>

          <p>
            Just here to confirm that roaming is active on your mobile service.
            Telstra has recently updated their method of activating roaming
            passes.
          </p>

          <p>
            Select a pack above and text the pack code to <strong>179</strong> to
            activate. Activating early will start the validity immediately.
          </p>

          <p className="intl-example-note">
            For example, if you're travelling for the next 5 days, you may wish
            to text <code>roam5</code> to <strong>179</strong>.
          </p>

          {/* EXAMPLE SMS CARD */}
          <div className="intl-example-card">
            <h3>Example SMS</h3>
            <p>Text the relevant code (e.g. <code>Roam5</code>) to 179.</p>

            <div className="intl-example-image-wrapper">
              <img
                src="/assets/roam-sms-example.png"
                alt="Example SMS conversation showing Roam5 sent to 179"
              />
            </div>
          </div>

          <div className="intl-warning">
            <span className="intl-warning-icon">⚠</span>
            <span>
              For best value, activate on the day you arrive or the day you plan
              to begin roaming.
            </span>
          </div>

          <div className="intl-text-block">
            <p>
              You will receive a notification once activation is successful.
              Your International Roaming Pack becomes active as soon as the SMS
              is processed.
            </p>
            <p>
              If you expect to use a lot of data, consider buying a local SIM/eSIM.
              If your usage will be light, switch to Aeroplane Mode and enable
              Wi-Fi. When connected to Wi-Fi, Telstra Wi-Fi Calling allows you to
              make and receive calls without roaming fees.
            </p>
          </div>
        </section>


        {/* MANAGING & TROUBLESHOOTING */}
        <section className="intl-section">
          <h2>Managing Data & Reception Overseas</h2>

          <div className="intl-two-col">

            <div className="intl-card">
              <h3>Control Data Usage</h3>
              <ul>
                <li>
                  Go to <strong>Settings &gt; Cellular</strong> to toggle data.
                </li>
                <li>
                  Adjust data mode under{" "}
                  <strong>Settings &gt; Cellular &gt; Cellular Data Options</strong>.
                </li>
                <li>
                  Enable <em>Low Data Mode</em> to reduce background usage.
                </li>
                <li>
                  Toggle <em>Data Roaming</em> on or off depending on your needs.
                </li>
              </ul>
            </div>

            <div className="intl-card">
              <h3>Troubleshooting Overseas Reception</h3>
              <ol>
                <li>Open <strong>Settings</strong>.</li>
                <li>Select <strong>Cellular</strong>.</li>
                <li>
                  Tap <strong>Cellular Network</strong> or <strong>Network Selection</strong>.
                </li>
                <li>Turn off <strong>Automatic</strong>.</li>
                <li>Select a carrier (e.g., Verizon, Vodafone).</li>
                <li>Wait up to 2 minutes for network registration.</li>
              </ol>
            </div>

          </div>
        </section>


        {/* COUNTRY LIST */}
        <section className="intl-section">
          <h2>Roaming Country List (68 Countries)</h2>
          <p className="intl-country-list">
            Austria, Belgium, Brazil, Bulgaria, Cambodia, Canada, Chile, China,
            Colombia, Croatia, Czech Republic, Denmark, Egypt, Estonia, Fiji,
            Finland, France, Germany, Greece, Hong Kong, Hungary, India,
            Indonesia, Ireland, Israel, Italy, Japan, Kenya, South Korea, Latvia,
            UK Channel Island (Guernsey), Lithuania, Luxembourg, Macao,
            Macedonia, Malaysia, Mexico, Netherlands, New Zealand, Nigeria,
            Norway, Papua New Guinea, Philippines, Poland, Portugal, Qatar,
            Romania, Russia, Samoa, Saudi Arabia, Singapore, Slovak Republic,
            Slovenia, South Africa, Spain, Sri Lanka, Sweden, Switzerland,
            Taiwan, Thailand, Tonga, Turkey, UAE, UK, USA, Vanuatu, Vietnam,
            Isle of Man.
          </p>
        </section>


        {/* CIS */}
        <section className="intl-section">
          <h2>Critical Information Summary</h2>
          <p className="intl-muted">
            Download the full CIS for complete roaming terms and inclusions.
          </p>

          <div className="intl-cis-actions">
            <a
              href="/docs/International Roaming Pack Critical Information Summary Landscape.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="intl-btn-primary"
            >
              Download Roaming CIS (PDF)
            </a>
          </div>

          <details className="intl-cis-details">
            <summary>View key CIS information</summary>
            <p>
              The CIS outlines roaming pricing, validity periods, inclusions,
              exclusions and important usage conditions. Always review it before
              travelling.
            </p>
          </details>
        </section>
      </div>
    </main>
  );
}
